/**
 * Dynamic Content Utilities
 *
 * This module contains utility functions for building and merging dynamic content slots,
 * overlaying DCA results, and normalizing CF items.
 */
import { SlotMeta, DcaNode, CfNode, SlotContent } from '../organisms/DynamicTabbedLayout/DynamicTabbedLayout.types';

// Pattern builder — defines grid structure (banner/sixpack combos)
export const buildPatternSlots = (bannerFirst: boolean, squareFirst: boolean, dcaNodes: DcaNode[] = []): SlotMeta[] => {
  const row1 = squareFirst ? ['square', 'tall', 'square'] : ['tall', 'square', 'tall'];
  const row2 = squareFirst ? ['tall', 'square', 'tall'] : ['square', 'tall', 'square'];
  const sixpack = [...row1, ...row2];

  // Prepare authored DCA orders for fast lookup
  const dcaOrders = new Set<number>();
  for (let i = 0; i < (dcaNodes?.length || 0); i++) {
    const num = Number(dcaNodes[i]?.order);
    if (!Number.isNaN(num)) dcaOrders.add(num);
  }

  const typeForPosition = (position: number): 'dca' | 'cf' => (dcaOrders.has(position) ? 'dca' : 'cf');

  const mapSixpack = (start: number): SlotMeta[] =>
    sixpack.map((size, i) => {
      const position = start + i;
      return {
        size: size as 'square' | 'tall',
        position,
        type: typeForPosition(position),
      };
    });

  if (bannerFirst) {
    const firstSixStart = 2;
    const secondSixStart = 9;
    return [
      { size: 'banner', position: 1, type: typeForPosition(1) },
      ...mapSixpack(firstSixStart),
      { size: 'banner', position: 8, type: typeForPosition(8) },
      ...mapSixpack(secondSixStart),
    ];
  }

  const secondSixStart = 8;
  return [...mapSixpack(1), { size: 'banner', position: 7, type: typeForPosition(7) }, ...mapSixpack(secondSixStart)];
};

// Merge CF + DCA into final slot model
export function mergeCfAndDcaToSlotsThreePass(
  slotsMeta: SlotMeta[],
  dcaNodes: DcaNode[],
  cfData: CfNode[]
): SlotContent[] | null {
  const len = slotsMeta?.length;
  const result = new Array(len);

  // Build a fast lookup map: order → DCA node (used only to populate dcaTarget)
  const dcaByOrder = new Map<number, DcaNode>();
  for (let i = 0; i < dcaNodes.length; i++) {
    const order = dcaNodes[i].order;
    if (order !== undefined && order !== null) {
      const num = Number(order);
      if (!Number.isNaN(num)) dcaByOrder.set(num, dcaNodes[i]);
    }
  }

  // First pass: initialize slots with structure only; no CF assignment yet
  for (let i = 0; i < len; i++) {
    const slot = slotsMeta[i];
    const position = typeof slot?.position === 'number' ? slot.position : i;
    const authoredDca = dcaByOrder.get(position);
    const isDca = slot?.type === 'dca';

    result[i] = {
      ...slot,
      position,
      type: isDca ? 'dca' : 'cf',
      payload: null, // will be filled for CF slots in pass 2
      dcaTarget:
        isDca && authoredDca
          ? {
              order: authoredDca.order,
              mboxParameter: authoredDca.mboxParameter,
            }
          : null,
      // cfFallback only meaningful for DCA slots; assigned in pass 3
      cfFallback: null,
    };
  }

  // Second pass: assign CF data to CF slots first, in order
  let cfIndex = 0;
  for (let i = 0; i < len && cfIndex < cfData.length; i++) {
    const slot = result[i];
    if (slot?.type === 'cf') {
      slot.payload = cfData[cfIndex] ?? null;
      cfIndex++;
    }
  }

  // Third pass: assign remaining CF data (if any) to cfFallback of DCA slots, in order
  for (let i = 0; i < len && cfIndex < cfData.length; i++) {
    const slot = result[i];
    if (slot?.type === 'dca') {
      slot.cfFallback = cfData[cfIndex] ?? null;
      cfIndex++;
    }
  }

  return result;
}

export function mergeCfAndDcaToSlots(
  slotsMeta: SlotMeta[],
  dcaNodes: DcaNode[],
  cfData: CfNode[]
): SlotContent[] | null {
  const len = slotsMeta?.length;
  const result = new Array(len);

  // Build a fast lookup map: order → DCA node
  const dcaByOrder = new Map<number, DcaNode>();
  for (let i = 0; i < dcaNodes.length; i++) {
    const order = dcaNodes[i].order;
    if (order !== undefined && order !== null) {
      const num = Number(order);
      if (!Number.isNaN(num)) dcaByOrder.set(num, dcaNodes[i]);
    }
  }

  for (let i = 0; i < len; i++) {
    const slot = slotsMeta[i];
    const position = typeof slot?.position === 'number' ? slot.position : i;
    const authoredDca = dcaByOrder.get(position);

    if (authoredDca) {
      result[i] = {
        ...slot,
        position,
        type: 'dca',
        payload: null,
        cfFallback: cfData[i] ?? null,
        dcaTarget: {
          order: authoredDca.order,
          mboxParameter: authoredDca.mboxParameter,
        },
      };
    } else {
      result[i] = {
        ...slot,
        position,
        type: 'cf',
        payload: cfData[i] ?? null,
        cfFallback: null,
      };
    }
  }

  return result;
}

// Overlay map onto slots (returns new slots array)
export function overlaySlotsWithDcaRes(
  slots: SlotContent[] = [],
  dcaNodes: DcaNode[] = [],
  opts?: { fillFromCFFallbackIfEmpty?: boolean }
) {
  const hasDca = Array.isArray(dcaNodes) && dcaNodes.length > 0;

  if (!hasDca && opts?.fillFromCFFallbackIfEmpty) {
    const filled = (slots || []).map(s => {
      if (s?.type === 'dca' && s?.cfFallback) {
        return { ...s, payload: s.cfFallback, type: 'dca' };
      }
      return s;
    });
    return filled;
  }

  const overlaid = (slots || []).map(s => {
    const match = dcaNodes.find(n => n.order === (s as SlotContent)?.position);
    if (match) {
      return { ...s, payload: match.payload, type: 'dca' };
    }
    return s;
  });
  return overlaid;
}

// Normalizes raw AEM GraphQL items into the CF structure.
export function normalizeCfItems(nodes: CfNode[], tabName: string): CfNode[] {
  if (!Array.isArray(nodes) || nodes.length === 0) return [];

  const count = nodes.length;
  const result = new Array(count);

  for (let i = 0; i < count; i++) {
    const item = nodes[i] || {};
    const icon = item.icon;
    const badgeIcon = item.badgeIcon;
    const brandLogoIcon = item.brandLogoIcon;
    const tags = item.tagsList;
    let tagListItems: { tagLabel: string }[] = [];
    if (tags && tags.length > 0) {
      const tCount = tags.length;
      tagListItems = new Array(tCount);
      for (let t = 0; t < tCount; t++) {
        tagListItems[t] = { tagLabel: tags[t] };
      }
    }

    result[i] = {
      ...item,
      id: item.id || `${tabName}-${Math.random().toString(36).slice(2)}`,
      tabName,
      icon: icon && icon.length ? icon[0] : '',
      description: (item.description as { plaintext?: string })?.plaintext || '',
      badgeIcon: badgeIcon && badgeIcon.length ? badgeIcon[0] : '',
      brandLogoIcon: brandLogoIcon && brandLogoIcon.length ? brandLogoIcon[0] : '',
      tagListItems: tagListItems || [],
    };
  }

  return result;
}

// Local mock builder for dev/test use
export function buildLocalDcaNodes(tabName: string, authoredTiles: DcaNode[] = []) {
  return (authoredTiles || []).map(dt => {
    const order = Number(dt.order ?? 0);
    const mbox = dt.mboxParameter;
    return {
      order,
      mbox,
      payload: {
        contentType: 'offers',
        title: `Mock Offer ${order}`,
        eyebrow: 'Local Mock',
        subTitle: `Tab: ${tabName}`,
        description: `This is a mock DCA payload for order ${order} (${mbox}).`,
        cardUrl: '#',
        openInNewTab: false,
        imageAltText: 'Mock image',
        assetPath: 'https://s7d1.scene7.com/is/image/marriotts7prod/bonvoy-young-women-w-headph32760-29545',
        trackingProperties: {},
        tagListItems: [],
      },
    };
  });
}
